import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flat-loader.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { FormField } from '@adornis/formfield/form-field.js';
import '@adornis/forms/x-input.js';
import type { XInput } from '@adornis/forms/x-input.js';
import { stateful, useEffect, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { searchParamsSignal, setRoutingState } from '@adornis/router/client/routing-state-signal.js';
import { NO_CHANGE } from '@adornis/router/no-change.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { examTypeBadgeStyle } from '@ils/examType/client/examTypeBadge.js';
import { hasGlobalRole } from '@ils/permissions/hasRole.js';
import { type ExamType, UserRole } from '@ils/static/enums.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { ref } from 'lit/directives/ref.js';
import { DateTime } from 'luxon';
import { filter, switchMap } from 'rxjs';
import { ExamDay } from '../../db/examdays.js';
import type { ILSUser } from '../../db/ils-user.js';
import { currentExamDay, type CurrentExamDay } from '../global-state.js';
import './ils-product-card.js';

/**
 * @element ils-examday-selector
 *
 * @property {boolean} main-selector - When true, the selector reads from and writes to the global routing state.
 *                                     When false, it just emits value-picked events.
 * @property {boolean} clearable - When true, allows clearing the selection.
 * @property {boolean} single-line - When true, only shows the date and time without location.
 */
@customElement('ils-examday-selector')
export class ILSExamDaySelector extends FormField<ExamDay> {
  @property({ type: Boolean }) clearable = false;
  @property({ type: Boolean, attribute: 'main-selector' }) mainSelector = false;
  @property({ type: Boolean, attribute: 'single-line' }) singleLine = false;

  @state() public examDays = new RXController(this, currentUser<ILSUser>().pipe(filter(Boolean)), user$ =>
    user$.pipe(
      switchMap(() =>
        ExamDay.subscribeExamDaysForCurrentUser()({
          _id: 1,
          _class: 1,
          name: 1,
          start: 1,
          end: 1,
          candidateIDs: 1,
          examType: 1,
          location: { displayName: 1 },
        }),
      ),
    ),
  );

  @state() currentUser = new RXController(this, currentUser<ILSUser>());
  @state() currentExamDay = new RXController(this, currentExamDay);

  override render() {
    if (!this.examDays.value) return html` <x-flat-loader></x-flat-loader> `;

    // If mainSelector is true, use currentExamDay as the default value
    // If mainSelector is false, use the component's value or null
    const selectedDay = this.mainSelector ? this.currentExamDay.value : this.value.value;

    return html`
      <x-flex
        horizontal
        crossaxis-center
        space="sm"
        padding="sm"
        ${css({
          cursor: 'pointer',
          border: `1px solid ${this.colors.neutral.shade(200)}`,
          borderRadius: this.sizes.borderRadius,
          background: this.colors.white,
          minWidth: '200px',
        })}
        @click=${() => this.openExamDaySelector()}
      >
        <x-flex flex direction="column" space="xxs" ${css({ width: '100%' })}>
          ${selectedDay
            ? renderExamDay(selectedDay, false, this.colors, this.singleLine)
            : html`<x-text>Termin auswählen</x-text>`}
        </x-flex>
        ${this.clearable && selectedDay
          ? html`
              <x-icon ${css({ cursor: 'pointer' })} @click=${(e: Event) => this.clearSelection(e)}> close </x-icon>
            `
          : nothing}
      </x-flex>
    `;
  }

  private clearSelection(e: Event) {
    e.stopPropagation(); // Prevent opening the selector dialog

    // Clear the internal value
    this.value.next(null);

    // Dispatch event
    this.dispatchEvent(new CustomEvent('value-picked', { detail: { value: null } }));
    this.requestUpdate();
  }

  private async openExamDaySelector() {
    if (!this.examDays.value) return;

    const user = this.currentUser.value;
    const validDate = DateTime.now();

    const filteredDays = this.examDays.value.filter(day => {
      // if the user is a trainee, only show examdays where the user is registered on
      return !hasGlobalRole(user, UserRole.TRAINEE) || (user && day.candidateIDs.includes(user._id));
    });

    const availableDays = filteredDays
      .filter(day => day.start > validDate)
      .sort((a, b) => {
        if (a.start > b.start) return 1;
        if (a.start < b.start) return -1;
        return 0;
      });
    const pastDays = filteredDays
      .filter(day => day.start <= validDate)
      .sort((a, b) => {
        if (a.start < b.start) return 1;
        if (a.start > b.start) return -1;
        return 0;
      });

    const selectedDay = await XNativeDialog.showPopup<ExamDay>({
      modal: true,
      content: (resolve, reject) =>
        ExamDaySelector({
          availableDays,
          pastDays,
          resolve,
          reject,
          colors: this.colors,
          sizes: this.sizes,
        }),
    }).catch(() => null);

    if (selectedDay) {
      // If this is the main selector, update the routing state
      if (this.mainSelector) {
        const currentState = searchParamsSignal.value;
        currentState.set('day', selectedDay._id);
        await setRoutingState({ path: NO_CHANGE, searchParams: currentState });
      }
      this.value.next(selectedDay);
      // Always emit the value-picked event
      this.dispatchEvent(new CustomEvent('value-picked', { detail: { value: selectedDay } }));
      this.requestUpdate();
    }
  }
}

const prettyDate = (date: DateTime) => date.toFormat('dd.LL.yyyy HH:mm');
const prettyTime = (date: DateTime) => date.toFormat('HH:mm');

// Helper function to get the tone for an exam type
const getColorForExamType = (examType: Maybe<ExamType>) => examTypeBadgeStyle(examType).style.background;

/**
 * Renders an exam day item with date, time, and location information
 */
const renderExamDay = (day: ExamDay | CurrentExamDay, isPast = false, colors: any, singleLine = false) => {
  const title = prettyDate(day.start) + ' - ' + prettyTime(day.end);
  const dotColor = getColorForExamType(day.examType);
  const textColor = isPast ? colors.neutral.shade(500) : undefined;

  return html`
    <x-flex space="xs" ${css({ color: textColor })}>
      <x-flex horizontal crossaxis-center space="xs">
        <div
          ${css({
            width: '10px',
            height: '10px',
            borderRadius: '2px',
            backgroundColor: dotColor,
            flexShrink: 0,
          })}
        ></div>
        <x-text bold ${css({ whiteSpace: 'nowrap' })}> ${title} </x-text>
      </x-flex>
      ${!singleLine
        ? html`
            <x-text ${css({ fontSize: '0.9em', color: isPast ? undefined : colors.neutral.shade(600) })}>
              ${day.location?.displayName ?? '---'}
            </x-text>
          `
        : nothing}
    </x-flex>
  `;
};

const ExamDaySelector = stateful<{
  availableDays: ExamDay[];
  pastDays: ExamDay[];
  resolve: (value: ExamDay) => void;
  reject: (reason?: any) => void;
}>(({ availableDays, pastDays, resolve, reject }) => {
  const design = useObservable(() => DesignSystem.currentTheme, []);
  const { colors, sizes, spacing } = design!;
  const [search, setSearch] = useState('');
  let searchInputRef: XInput | null = null;

  // Focus the search input when the component is mounted
  useEffect(() => {
    if (searchInputRef) searchInputRef.focus();
  }, []);

  const updateSearch = (e: ValueEvent<string | undefined>) => {
    setSearch(e.detail.value?.toLowerCase() ?? '');
  };

  const filterExamDays = (days: ExamDay[]) => {
    if (!search) return days;

    return days.filter(day => {
      const locationStr = day.location?.displayName?.toLowerCase() ?? '';

      return day.start.toFormat('ddLLyyyyhhmm').includes(search.replace(/\W/g, '')) || locationStr.includes(search);
    });
  };

  const wrapper = (content: Renderable, clickHandler = () => {}) => html`
    <x-flex
      ${css({
        cursor: 'pointer',
        height: '100%',
        border: `1px solid ${colors.neutral.shade(200)}`,
        borderRadius: sizes.borderRadius,
        padding: '0.75rem',
      })}
      @click=${clickHandler}
    >
      ${content}
    </x-flex>
  `;

  const renderExamDayGridItems = (days: ExamDay[], isPast = false) => {
    return days.map(day => {
      return wrapper(renderExamDay(day, isPast, colors), () => resolve(day));
    });
  };

  const renderFilteredDays = (days: ExamDay[], isPast = false) => {
    const filteredDays = filterExamDays(days);
    if (filteredDays.length === 0) {
      return wrapper(html` <x-text> Keine ${isPast ? 'vergangenen' : 'kommenden'} Termine gefunden </x-text> `);
    }
    return renderExamDayGridItems(filteredDays, isPast);
  };

  return html`
    <x-flex flex-none padding="md" space="md" ${css({ height: '90vh', width: '70vw' })}>
      <x-h3> Termin auswählen </x-h3>

      <x-input
        ${ref((el: any) => {
          if (el) searchInputRef = el;
        })}
        placeholder="Suche nach Datum oder Ort..."
        @value-changed=${updateSearch}
        ${css({ width: '100%' })}
      ></x-input>
      <x-grid auto-fill-columns="250px" space="sm" ${css({ overflowY: 'auto' })}>
        ${availableDays.length > 0
          ? html` <div ${css({ gridColumn: '1 / -1' })}>${wrapper(html`<x-text>Kommende Termine</x-text>`)}</div>
              ${renderFilteredDays(availableDays)}`
          : html` <x-text>Keine verfügbaren Termine</x-text> `}
        ${pastDays.length > 0
          ? html` <div ${css({ gridColumn: '1 / -1' })}>${wrapper(html`<x-text>Vergangene Termine</x-text>`)}</div>
              ${renderFilteredDays(pastDays, true)}`
          : nothing}
      </x-grid>
      <div flex ${css({ marginTop: `-${spacing.md}` })}></div>
      <x-button mode="outline" @click=${() => reject()}>Abbrechen</x-button>
    </x-flex>
  `;
});
